export type AiService = {
  title: string;
  shortTitle: string;
  href: string;
  icon: string;
  tagline: string;
  description: string;
  heroPoints: string[];
  useCases: string[];
  deliverables: string[];
  stack: string[];
  outcomes: string[];
};

export const aiServices: AiService[] = [
  {
    title: "AI Chatbot Development",
    shortTitle: "AI Chatbots",
    href: "/services/ai-chatbot-development",
    icon: "fas fa-comments",
    tagline: "Customer support and lead qualification bots trained on your business data.",
    description:
      "Build AI chatbots for websites, CRM workflows, WhatsApp, support desks and internal teams. We connect your knowledge base, FAQs, CRM and business rules so the bot can answer, qualify, route and summarize conversations.",
    heroPoints: ["Website and WhatsApp bots", "RAG knowledge-base answers", "CRM lead capture and routing"],
    useCases: ["Customer support automation", "Lead qualification", "FAQ and policy assistants", "Internal HR or IT helpdesk"],
    deliverables: ["Conversation flow and prompt design", "Knowledge-base setup", "CRM or ticketing integration", "Admin handoff and analytics"],
    stack: ["OpenAI", "Gemini", "RAG", "Vector Search", "Node.js", "React", "WhatsApp API"],
    outcomes: ["Faster response times", "Lower repetitive support load", "Better lead capture", "24/7 self-service"],
  },
  {
    title: "WhatsApp AI Response Automation",
    shortTitle: "WhatsApp AI",
    href: "/services/whatsapp-ai-response",
    icon: "fab fa-whatsapp",
    tagline: "AI replies for WhatsApp leads, support chats and CRM follow-ups.",
    description:
      "Add AI-powered response automation to WhatsApp Business API. We help businesses answer FAQs, qualify leads, summarize conversations, route chats to teams and sync useful data back to CRM systems.",
    heroPoints: ["WhatsApp AI replies", "Lead qualification and routing", "Human handoff with CRM sync"],
    useCases: ["WhatsApp customer support", "Lead qualification chats", "Product or pricing FAQs", "Appointment and follow-up reminders"],
    deliverables: ["WhatsApp AI flow design", "Knowledge-base and prompt setup", "CRM integration", "Human handoff and escalation rules"],
    stack: ["WhatsApp Business API", "VSCRM", "OpenAI", "RAG", "Node.js", "Webhooks", "PostgreSQL"],
    outcomes: ["Faster WhatsApp replies", "Better lead handling", "Reduced repetitive chat load", "Traceable CRM conversations"],
  },
  {
    title: "AI Agent & Workflow Automation",
    shortTitle: "AI Agents",
    href: "/services/ai-agent-automation",
    icon: "fas fa-robot",
    tagline: "Agentic workflows that automate repetitive business operations.",
    description:
      "Create AI agents that assist with follow-ups, data entry, ticket triage, task creation, report preparation and approval workflows. We keep automation practical, auditable and integrated with your existing tools.",
    heroPoints: ["Task and workflow agents", "Approval and escalation logic", "Human-in-the-loop controls"],
    useCases: ["Sales follow-up automation", "Support ticket classification", "Report generation", "Operations task routing"],
    deliverables: ["Workflow mapping", "Agent prompts and tool access", "Integration with CRM/ERP/API systems", "Monitoring and fail-safe rules"],
    stack: ["OpenAI", "Gemini", "LangChain", "n8n", "Node.js", "PostgreSQL", "REST APIs"],
    outcomes: ["Reduced manual work", "Fewer missed follow-ups", "Consistent process execution", "Better team productivity"],
  },
  {
    title: "Generative AI Integration",
    shortTitle: "GenAI Integration",
    href: "/services/generative-ai-integration",
    icon: "fas fa-wand-magic-sparkles",
    tagline: "Add generative AI features into your software, CRM or SaaS product.",
    description:
      "Embed generative AI into existing applications for content drafting, summaries, recommendations, semantic search, smart forms, chat assistants and AI-powered dashboards.",
    heroPoints: ["AI features inside existing apps", "Summaries and recommendations", "Secure prompt and model integration"],
    useCases: ["CRM note summaries", "Proposal and email drafting", "Knowledge search", "Product recommendations"],
    deliverables: ["AI feature design", "API integration", "Prompt and guardrail setup", "Usage tracking and cost controls"],
    stack: ["OpenAI API", "Gemini API", "Claude API", "Embeddings", "PostgreSQL", "React", "Node.js"],
    outcomes: ["Smarter products", "Faster user workflows", "Better information retrieval", "Improved customer experience"],
  },
  {
    title: "AI CRM & Lead Scoring",
    shortTitle: "AI CRM",
    href: "/services/ai-crm-lead-scoring",
    icon: "fas fa-chart-line",
    tagline: "AI-powered CRM workflows for sales teams that need better follow-up discipline.",
    description:
      "Upgrade CRM systems with AI lead scoring, follow-up suggestions, conversation summaries, opportunity insights, WhatsApp automation and sales dashboards.",
    heroPoints: ["Lead scoring", "Follow-up recommendations", "Conversation summaries"],
    useCases: ["Sales prioritization", "Missed follow-up alerts", "Call and chat summaries", "Pipeline intelligence"],
    deliverables: ["CRM data mapping", "Lead scoring model logic", "Automation rules", "Sales dashboard and reports"],
    stack: ["VSCRM", "OpenAI", "PostgreSQL", "WhatsApp API", "Dialer APIs", "BI Dashboards"],
    outcomes: ["Higher lead response quality", "Better pipeline visibility", "Improved sales productivity", "Cleaner customer data"],
  },
  {
    title: "AI Document Processing",
    shortTitle: "Document AI",
    href: "/services/ai-document-processing",
    icon: "fas fa-file-invoice",
    tagline: "Extract, classify and validate business documents with AI.",
    description:
      "Automate invoice reading, KYC document checks, resume parsing, forms, PDFs and email attachments. We connect extraction results to your CRM, ERP, dashboard or approval workflow.",
    heroPoints: ["PDF and invoice extraction", "KYC and form processing", "ERP/CRM data sync"],
    useCases: ["Invoice processing", "Resume parsing", "KYC verification support", "Email attachment classification"],
    deliverables: ["Document pipeline design", "OCR and extraction setup", "Validation and exception handling", "Dashboard or API output"],
    stack: ["Document AI", "OCR", "OpenAI", "Python", "Node.js", "PostgreSQL", "Cloud Storage"],
    outcomes: ["Less manual data entry", "Faster document turnaround", "Cleaner structured data", "Traceable approvals"],
  },
  {
    title: "AI Data Analytics & BI",
    shortTitle: "AI Analytics",
    href: "/services/ai-data-analytics",
    icon: "fas fa-database",
    tagline: "Decision dashboards, forecasting and natural-language business insights.",
    description:
      "Turn CRM, ERP, website, sales and operations data into useful dashboards and AI-assisted insights. We build reporting systems that help leadership ask better questions and act faster.",
    heroPoints: ["Executive dashboards", "Forecasting and anomaly alerts", "Natural-language data queries"],
    useCases: ["Sales analytics", "Operations dashboards", "Customer segmentation", "Forecasting and trend reports"],
    deliverables: ["Data model and ETL setup", "BI dashboards", "AI insight layer", "Scheduled reports and alerts"],
    stack: ["PostgreSQL", "Python", "Power BI", "Metabase", "OpenAI", "APIs", "Data Pipelines"],
    outcomes: ["Better decisions", "Single view of business data", "Faster reporting", "Early risk visibility"],
  },
];

export function getAiServiceByHref(href: string) {
  return aiServices.find((service) => service.href === href);
}
